import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QRScanner = ({ onScanComplete }) => {
  const { currentTenant } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleManualScan = async () => {
    if (!manualCode?.trim()) {
      setError('Please enter a QR code');
      return;
    }

    setScanning(true);
    setError('');
    setResult(null);

    try {
      // Find staff QR code
      const { data: qrCode, error: qrError } = await supabase?.from('staff_qr_codes')?.select(`
          *,
          user_profiles!staff_qr_codes_user_id_fkey(id, full_name, email)
        `)?.eq('tenant_id', currentTenant?.id)?.eq('qr_code_data', manualCode?.trim())?.eq('is_active', true)?.single();

      if (qrError || !qrCode) {
        throw new Error('Invalid or inactive QR code');
      }

      // Check today's attendance record
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const { data: existingRecord, error: recordError } = await supabase?.from('staff_attendance')?.select('*')?.eq('tenant_id', currentTenant?.id)?.eq('user_id', qrCode?.user_id)?.gte('check_in_time', today?.toISOString())?.order('check_in_time', { ascending: false })?.limit(1)?.maybeSingle();

      let action = '';
      let updateData = {};

      if (!existingRecord) {
        // First scan of the day - Check In
        const { error: insertError } = await supabase?.from('staff_attendance')?.insert({
            tenant_id: currentTenant?.id,
            user_id: qrCode?.user_id,
            check_in_time: new Date()?.toISOString()
          });

        if (insertError) throw insertError;
        action = 'Checked In';
      } else if (existingRecord?.check_in_time && !existingRecord?.lunch_start_time) {
        // Second scan - Start Lunch Break
        updateData = { lunch_start_time: new Date()?.toISOString() };
        action = 'Lunch Break Started';
      } else if (existingRecord?.lunch_start_time && !existingRecord?.lunch_end_time) {
        // Third scan - End Lunch Break
        const lunchStart = new Date(existingRecord?.lunch_start_time);
        const lunchEnd = new Date();
        const lunchMinutes = Math.round((lunchEnd - lunchStart) / (1000 * 60));
        
        updateData = { 
          lunch_end_time: lunchEnd?.toISOString(),
          lunch_duration_minutes: lunchMinutes
        };
        action = 'Lunch Break Ended';
      } else if (existingRecord?.lunch_end_time && !existingRecord?.check_out_time) {
        // Fourth scan - Check Out
        const checkIn = new Date(existingRecord?.check_in_time);
        const checkOut = new Date();
        const totalMinutes = Math.round((checkOut - checkIn) / (1000 * 60));
        const lunchMinutes = existingRecord?.lunch_duration_minutes || 0;
        const workMinutes = totalMinutes - lunchMinutes;
        
        updateData = { 
          check_out_time: checkOut?.toISOString(),
          total_hours: (workMinutes / 60)?.toFixed(2)
        };
        action = 'Checked Out';
      } else {
        throw new Error('Already checked out for today');
      }

      if (Object.keys(updateData)?.length > 0) {
        const { error: updateError } = await supabase?.from('staff_attendance')?.update(updateData)?.eq('id', existingRecord?.id);

        if (updateError) throw updateError;
      }

      setResult({
        success: true,
        action,
        staffName: qrCode?.user_profiles?.full_name,
        time: new Date()?.toLocaleTimeString()
      });

      setManualCode('');
      onScanComplete?.();
    } catch (error) {
      console.error('Error processing scan:', error);
      setError(error?.message || 'Failed to process QR code');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-6 border border-border">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="ScanLine" size={24} className="text-primary" />
        <h2 className="text-xl font-bold text-foreground">QR Code Scanner</h2>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Enter QR Code Manually
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && handleManualScan()}
              placeholder="Paste or type QR code data"
              className="flex-1 px-3 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            />
            <Button
              onClick={handleManualScan}
              disabled={scanning || !manualCode?.trim()}
            >
              {scanning ? 'Processing...' : 'Scan'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="XCircle" size={20} className="text-red-500 mt-0.5" />
              <div>
                <p className="font-semibold text-red-500 mb-1">Scan Failed</p>
                <p className="text-sm text-foreground">{error}</p>
              </div>
            </div>
          </div>
        )}

        {result?.success && (
          <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={20} className="text-green-500 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-green-500 mb-1">{result?.action}</p>
                <p className="text-sm text-foreground mb-1">{result?.staffName}</p>
                <p className="text-xs text-muted-foreground">{result?.time}</p>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-blue-500 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-500 mb-2">Scan Sequence</p>
              <ul className="text-xs text-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <Icon name="LogIn" size={12} className="text-green-500" />
                  <span>1st Scan: Check In</span>
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Coffee" size={12} className="text-orange-500" />
                  <span>2nd Scan: Start Lunch Break</span>
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Coffee" size={12} className="text-blue-500" />
                  <span>3rd Scan: End Lunch Break</span>
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="LogOut" size={12} className="text-red-500" />
                  <span>4th Scan: Check Out</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;